import {
    memo,
    type MouseEvent as ReactMouseEvent,
    type RefObject,
    type TouchEvent as ReactTouchEvent,
    useEffect,
    useState
} from 'react'
import { InternalHandle } from '../lib/internal-handle.tsx'
import {
    DEFAULT_MAGNETIC_HANDLE_CONFIG,
    type MagneticHandlePoint,
    type MagneticHandleSide,
    resolveMagnetizedHandlePoint,
} from '../model/model'

export type InternalMagneticHandleTriggerZoneHoverState = {
    magnetized: boolean
    point: MagneticHandlePoint
    side: MagneticHandleSide
} | null

type InternalMagneticHandleTriggerZoneProps = {
    defaultPoint: MagneticHandlePoint
    effectiveNodeHeight: number
    effectiveNodeWidth: number
    hidden?: boolean
    nodeId: string
    nodeRef: RefObject<HTMLDivElement | null>
    onHoverStateChange?: (state: InternalMagneticHandleTriggerZoneHoverState) => void
    resolveCurrentZoom: () => number
    side: MagneticHandleSide
    limitVisible: boolean,
}

type ForwardablePointerLike = {
    clientX: number
    clientY: number
}

type UpdateLocalHoverStateParams = {
    effectiveNodeHeight: number
    effectiveNodeWidth: number
    nodeRef: RefObject<HTMLDivElement | null>
    setLocalHoverState: React.Dispatch<React.SetStateAction<InternalMagneticHandleTriggerZoneHoverState>>
    side: MagneticHandleSide
    pointer: ForwardablePointerLike
    zoom: number
}

type ForwardTriggerPointerParams = {
    event: ForwardablePointerLike
    nodeId: string
    side: MagneticHandleSide
}

type HoverPointerHandlerParams = Omit<UpdateLocalHoverStateParams, 'pointer'> & {
    event: ForwardablePointerLike
}

type ClearHoverStateHandlerParams = {
    setLocalHoverState: React.Dispatch<React.SetStateAction<InternalMagneticHandleTriggerZoneHoverState>>
    side: MagneticHandleSide
}

type TriggerMouseDownHandlerParams = {
    event: ReactMouseEvent<HTMLDivElement>
    nodeId: string
    side: MagneticHandleSide
}

type TriggerTouchStartHandlerParams = {
    event: ReactTouchEvent<HTMLDivElement>
    nodeId: string
    side: MagneticHandleSide
}

function resolveLogicalHandleConfig(side: MagneticHandleSide) {
    return side === 'left'
        ? { handleType: 'target' as const, logicalHandleId: 'canvas-target-logical' }
        : { handleType: 'source' as const, logicalHandleId: 'canvas-source-logical' }
}

function updateLocalHoverState({
    effectiveNodeHeight,
    effectiveNodeWidth,
    nodeRef,
    pointer,
    setLocalHoverState,
    side,
    zoom,
}: UpdateLocalHoverStateParams) {
    const element = nodeRef.current
    if (!element) {
        return
    }

    const rect = element.getBoundingClientRect()
    const magnetResult = resolveMagnetizedHandlePoint({
        allowedSide: side,
        clientX: pointer.clientX,
        clientY: pointer.clientY,
        config: DEFAULT_MAGNETIC_HANDLE_CONFIG,
        nodeHeight: effectiveNodeHeight,
        nodeWidth: effectiveNodeWidth,
        rect,
        zoom,
    })

    setLocalHoverState({
        magnetized: magnetResult.side === side,
        point: magnetResult.point,
        side,
    })
}

function clearLocalHoverState(
    side: MagneticHandleSide,
    setLocalHoverState: React.Dispatch<React.SetStateAction<InternalMagneticHandleTriggerZoneHoverState>>,
) {
    setLocalHoverState((current) => (current?.side === side ? null : current))
}

function forwardTriggerPointerToLogicalHandle({ event, nodeId, side }: ForwardTriggerPointerParams) {
    const handleConfig = resolveLogicalHandleConfig(side)
    forwardPointerToLogicalHandle(nodeId, handleConfig.logicalHandleId, event)
}

function handleHoverPointer({
    effectiveNodeHeight,
    effectiveNodeWidth,
    event,
    nodeRef,
    setLocalHoverState,
    side,
    zoom,
}: HoverPointerHandlerParams) {
    updateLocalHoverState({
        effectiveNodeHeight,
        effectiveNodeWidth,
        nodeRef,
        pointer: event,
        setLocalHoverState,
        side,
        zoom,
    })
}

function handleClearHoverState({ setLocalHoverState, side }: ClearHoverStateHandlerParams) {
    clearLocalHoverState(side, setLocalHoverState)
}

function handleTriggerMouseDown({ event, nodeId, side }: TriggerMouseDownHandlerParams) {
    event.preventDefault()
    event.stopPropagation()
    forwardTriggerPointerToLogicalHandle({
        event: event.nativeEvent,
        nodeId,
        side,
    })
}

function handleTriggerTouchStart({ event, nodeId, side }: TriggerTouchStartHandlerParams) {
    const touch = event.touches[0]
    if (!touch) {
        return
    }

    event.preventDefault()
    event.stopPropagation()
    forwardTriggerPointerToLogicalHandle({
        event: touch,
        nodeId,
        side,
    })
}

function forwardPointerToLogicalHandle(nodeId: string, logicalHandleId: string, event: ForwardablePointerLike) {
    const handleElement = document.querySelector<HTMLElement>(
        `.react-flow__handle[data-nodeid="${nodeId}"][data-handleid="${logicalHandleId}"]`,
    )

    if (!handleElement) {
        return
    }

    const nextEvent = new MouseEvent('mousedown', {
        bubbles: true,
        button: 0,
        buttons: 1,
        cancelable: true,
        clientX: event.clientX,
        clientY: event.clientY,
        composed: true,
        view: window,
    })

    handleElement.dispatchEvent(nextEvent)
}

export const HandleTriggerZone = memo(function HandleTriggerZone({
    defaultPoint,
    effectiveNodeHeight,
    effectiveNodeWidth,
    hidden = false,
    nodeId,
    nodeRef,
    onHoverStateChange,
    resolveCurrentZoom,
    side,
    limitVisible,
}: InternalMagneticHandleTriggerZoneProps) {
    const [localHoverState, setLocalHoverState] = useState<InternalMagneticHandleTriggerZoneHoverState>(null)
    const point = localHoverState ? localHoverState.point : defaultPoint

    const handlePointerHover = (event: ForwardablePointerLike) => {
        handleHoverPointer({
            effectiveNodeHeight,
            effectiveNodeWidth,
            event,
            nodeRef,
            setLocalHoverState,
            side,
            zoom: resolveCurrentZoom(),
        })
    }

    useEffect(() => {
        onHoverStateChange?.(localHoverState)

    }, [localHoverState, onHoverStateChange])

    useEffect(() => {
        return () => {
            onHoverStateChange?.(null)
        }
    }, [onHoverStateChange])

    useEffect(() => {
        if (!hidden) {
            return
        }

        clearLocalHoverState(side, setLocalHoverState)
    }, [hidden, side])

    return (
        <>
            <InternalHandle side={side} />
            {hidden || !limitVisible ? null : (
                <div
                    className={`nodrag nopan absolute left-0 top-0 z-[5] opacity-0 pointer-events-auto ${localHoverState ? 'cursor-crosshair' : 'cursor-default'}`}
                    draggable={false}
                    onMouseDown={(event) => handleTriggerMouseDown({ event, nodeId, side })}
                    onMouseEnter={(event) => handlePointerHover(event.nativeEvent)}
                    onMouseLeave={() => handleClearHoverState({ setLocalHoverState, side })}
                    onMouseMove={(event) => handlePointerHover(event.nativeEvent)}
                    onTouchStart={(event) => handleTriggerTouchStart({ event, nodeId, side })}
                    style={{
                        height: `${DEFAULT_MAGNETIC_HANDLE_CONFIG.magnetRadius * 2}px`,
                        width: `72px`,
                        transform: `translate3d(${side === 'left' ? -72 : effectiveNodeWidth}px, ${point.y - DEFAULT_MAGNETIC_HANDLE_CONFIG.magnetRadius}px, 0)`,
                    }}
                />
            )}
        </>
    )
})
